// frontend/src/pages/PaymentSuccess.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/PaymentPages.css';

/**
 * Payment Success Page
 * Displayed after successful Stripe Checkout completion
 *
 * Features:
 * - Auto-redirects to original page after 3 seconds
 * - Validates saved path for security
 * - Fallback to /collections if no valid path
 * - Manual "Return Now" button to skip countdown
 */
export const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [redirectPath, setRedirectPath] = useState<string>('/collections');

  useEffect(() => {
    document.title = 'Payment Successful - AIPrep';

    // Read and validate return path from localStorage
    const savedPath = localStorage.getItem('aiprep_return_after_upgrade');
    const isValidPath = savedPath && savedPath.startsWith('/') && !savedPath.startsWith('//');
    const targetPath = isValidPath ? savedPath : '/collections';

    setRedirectPath(targetPath);
    console.log('[PaymentSuccess] Will redirect to:', targetPath);

    // Auto-redirect after 3 seconds
    const timer = setTimeout(() => {
      localStorage.removeItem('aiprep_return_after_upgrade');
      console.log('[PaymentSuccess] Auto-redirecting to:', targetPath);
      navigate(targetPath);
    }, 3000);

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, [navigate]);

  const handleContinue = () => {
    localStorage.removeItem('aiprep_return_after_upgrade');
    console.log('[PaymentSuccess] Manual redirect to:', redirectPath);
    navigate(redirectPath);
  };

  return (
    <div className="payment-page">
      <div className="payment-card success">
        <div className="icon-container">
          <svg className="success-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="#10b981" />
            <path
              d="M8 12.5l3 3 5-6"
              stroke="white"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="payment-title">Welcome to Pro!</h1>

        <p className="payment-message">
          Your payment was successful. You now have access to all Pro features including:
        </p>

        <ul className="features-list">
          <li>10,000 AI helps per month</li>
          <li>Solution verification</li>
          <li>AI tutor chat</li>
          <li>Algorithm animations</li>
          <li>Document extraction</li>
          <li>Collection generation</li>
        </ul>

        <div className="payment-details">
          <p className="detail-item">
            <strong>Plan:</strong> Pro Monthly
          </p>
          <p className="detail-item">
            <strong>Amount:</strong> $29.99/month
          </p>
          <p className="detail-item">
            <strong>Next billing:</strong> One month from today
          </p>
        </div>

        <button
          className="continue-button"
          onClick={handleContinue}
          aria-label="Return now without waiting"
        >
          Return Now
        </button>

        <p className="support-text">
          You'll receive a confirmation email shortly with your receipt and subscription details.
        </p>
      </div>
    </div>
  );
};
